import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Badge, Button, Card, CardBody, CardHeader, CardTitle, Table } from "reactstrap";
import { ThreeDots } from "react-loader-spinner";
import { toast } from "react-toastify";

import useStoreCameraTree from "../cameraPrototypes/shared/useStoreCameraTree";
import useInferenceMachineCache from "../cameraPrototypes/shared/useInferenceMachineCache";
import { getRecorderBrands } from "../cameraPrototypes/shared/captureDeviceService";
import DeviceDiscoveryPanel from "../cameraPrototypes/DeviceDiscoveryPanel";
import BulkCaptureDeviceForm from "../cameraPrototypes/BulkCaptureDeviceForm";
import CameraScreenshot from "./CameraScreenshot";
import limitCapture from "./captureLimiter";
import { MAX_BATCH, captureScreenshots, describeRequestError, describeScreenshotError } from "./screenshotOperations";

/** The server rejects a whole batch holding an inactive camera or one without inference machine. */
function isCapturable(camera) {
  return Boolean(camera.is_active) && camera.inference_machine != null;
}

function chunk(values, size) {
  const chunks = [];
  for (let start = 0; start < values.length; start += size) chunks.push(values.slice(start, start + size));
  return chunks;
}

/**
 * Every camera of a device, active or not.
 *
 * Parity with the legacy screenshots page, which listed every channel regardless
 * of state: a mirrored/newly-scanned camera lands as `is_active: false`, so an
 * active-only filter would hide it and leave the page empty for stores whose
 * camera-model cameras have not been activated yet.
 */
function camerasOf(device) {
  return device.cameras || [];
}

/** RecorderBrand is serialized either as a bare string or as `{ name, kind }`. */
function brandName(brand) {
  if (!brand) return "—";
  return typeof brand === "string" ? brand : brand.name || "—";
}

/**
 * The `kind` of a device's brand, or null when it cannot be resolved.
 *
 * The tree serializes `brand` as its primary key, so the kind comes from the `recorder-brands`
 * table. `DeviceDiscoveryPanel` fails open on null, so a failed fetch offers the RTSP suffix
 * rather than hiding it on an IP device.
 *
 * @param {number|string|null} brand - The device's `brand` field, as the tree sends it.
 * @param {Object<string, string>} brandKinds - Map from brand id (as a string) to its kind.
 * @returns {string|null} The brand kind, or null.
 */
function brandKindOf(brand, brandKinds) {
  if (brand == null) return null;
  return brandKinds[String(brand)] || null;
}

/** A camera has an analysis polygon when any of its views carries a non-empty view_area. */
function cameraHasPolygon(camera) {
  return (camera.views || []).some((view) => Array.isArray(view.view_area) && view.view_area.length > 0);
}

/** Join the camera's view names for the "Name" column (legacy channel_name parity). */
function cameraName(camera) {
  const names = (camera.views || []).map((view) => view.view_name).filter(Boolean);
  return names.length > 0 ? names.join(", ") : "—";
}

/**
 * Camera-model variant of the store screenshots page.
 *
 * Reads the Store → CaptureDevice → Camera tree (read-only) and captures screenshots through
 * manager-api's asynchronous screenshot operations, one camera or a whole batch at a time.
 */
export default function CameraModelScreenshots({ storeId, storeLocation }) {
  const { tree, loading, error, reset } = useStoreCameraTree(storeId);
  const machineCache = useInferenceMachineCache(tree);
  // Brand id → kind, for the discovery panel's suffix rule. Read once; a failure leaves the
  // map empty, which the panel treats as "kind unknown" and handles by failing open.
  const [brands, setBrands] = useState([]);
  // Whether the bulk creation form is open.
  const [adding, setAdding] = useState(false);
  // Which device cards have their discovery panel open, keyed by device public_id.
  const [discovering, setDiscovering] = useState({});
  // Outcome of the last "Screenshot all" per camera public_id, handed to its row.
  const [batchResults, setBatchResults] = useState({});
  // Flipped once a load has completed, so a later refetch is not mistaken for the first one.
  const settled = useRef(false);

  useEffect(() => {
    let cancelled = false;
    getRecorderBrands()
      .then((fetched) => {
        if (cancelled) return;
        // Only commit when the lookup taught us something: state already starts empty, so an
        // empty result would re-render for nothing (and, under test, commit outside act()).
        if ((fetched || []).length > 0) setBrands(fetched);
      })
      .catch(() => {
        // Non-fatal: the panel fails open on an unknown kind.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /**
   * Brand id → kind, for the discovery panel's suffix rule. Derived rather than stored: the bulk
   * creation form needs the brands themselves (name + kind) for its shared brand select.
   */
  const brandKinds = useMemo(() => {
    const map = {};
    brands.forEach((brand) => {
      if (brand?.id != null) map[String(brand.id)] = brand.kind;
    });
    return map;
  }, [brands]);

  /**
   * The batch landed: refetch the tree so the new devices and their cameras show up.
   *
   * The form is deliberately left open — a store is provisioned range by range, and closing it would
   * mean retyping the credentials, ports and suffix every time. No discovery panel is opened either:
   * the probe already produced the camera on slot 1, so there is nothing left to discover.
   */
  const handleBulkCreated = ({ publicIds, created }) => {
    // The devices exist either way; only the correlation can come back short.
    if (created != null && (publicIds || []).length < created) {
      toast.warn(
        `${created} capture devices were created, but the server named only ${
          (publicIds || []).length
        } of them — check the list below.`
      );
    }
    if (reset) reset();
  };

  /**
   * Close a discovery panel.
   *
   * @param {string} devicePublicId - The device whose panel is closing.
   */
  const closeDiscovery = (devicePublicId) => setDiscovering((prev) => ({ ...prev, [devicePublicId]: false }));

  const toggleDiscovery = (devicePublicId) => {
    if (discovering[devicePublicId]) {
      closeDiscovery(devicePublicId);
      return;
    }
    setDiscovering((prev) => ({ ...prev, [devicePublicId]: true }));
  };

  const setOutcomes = (outcomes) => setBatchResults((prev) => ({ ...prev, ...outcomes }));

  /**
   * Capture every capturable camera given, in batches of {@link MAX_BATCH}: one operation per
   * batch rather than one per camera, so a big store stays well under the server's quota.
   *
   * @param {object[]} cameras - The cameras to capture.
   */
  const captureCameras = (cameras) => {
    const ids = cameras
      .filter((camera) => isCapturable(camera) && batchResults[camera.public_id]?.status !== "load")
      .map((camera) => camera.public_id);
    setOutcomes(Object.fromEntries(ids.map((id) => [id, { status: "load" }])));
    chunk(ids, MAX_BATCH).forEach((batch) => {
      // A camera shown mid-batch is never written again by this batch: a later tracking error
      // must not turn it failed, and a newer capture of it may already own its row.
      const delivered = new Set();
      const deliver = (outcomes) => {
        Object.keys(outcomes).forEach((id) => delivered.add(id));
        setOutcomes(outcomes);
      };
      limitCapture(() => captureScreenshots(storeId, batch, deliver))
        .catch((ex) => {
          const message = describeRequestError(ex);
          return Object.fromEntries(batch.map((id) => [id, { status: "fail", error: message }]));
        })
        .then((outcomes) =>
          setOutcomes(
            Object.fromEntries(
              batch
                .filter((id) => !delivered.has(id))
                .map((id) => [id, outcomes[id] || { status: "fail", error: describeScreenshotError(null) }])
            )
          )
        );
    });
  };

  const devices = tree || [];
  // Only the *first* load takes over the page. `reset()` is the hook's `load()`, so every refetch
  // flips `loading` back to true: early-returning here would unmount the bulk form mid-session and
  // hand the operator an empty one back, credentials and all. Same for an error on a refetch — the
  // tree already on screen is still valid.
  //
  // "First" is tracked, not inferred from an empty tree: provisioning a store starts with zero
  // devices, so emptiness would make every refetch there look like a first load.
  if (!loading) settled.current = true;
  const firstLoad = !settled.current;

  if (loading && firstLoad) {
    return (
      <div className="content">
        <ThreeDots height="20" width="40" radius="5" color="#1d8cf8" ariaLabel="loading" visible />
      </div>
    );
  }

  if (error && firstLoad) {
    return (
      <div className="content">
        <Alert color="danger">Failed to load the camera-model tree for {storeLocation}.</Alert>
      </div>
    );
  }

  return (
    <div className="content">
      <Button className="btn-info mb-3 mr-2" onClick={() => setAdding((open) => !open)}>
        <i className="fa fa-plus mr-2" />
        Add IP cameras
      </Button>
      {loading && (
        <span className="mr-2 text-muted" style={{ fontSize: "0.8rem" }}>
          <ThreeDots height="12" width="20" radius="5" color="#1d8cf8" ariaLabel="refreshing" visible /> refreshing…
        </span>
      )}
      {error && !firstLoad && (
        <span className="mr-2 text-warning" style={{ fontSize: "0.8rem" }}>
          Could not refresh the tree — showing the last one loaded.
        </span>
      )}

      <Button
        className="btn-info mb-3 mr-2"
        title="Capture every active camera of every device (queued server-side, can take minutes)"
        disabled={!devices.some((device) => camerasOf(device).some(isCapturable))}
        onClick={() => captureCameras(devices.flatMap(camerasOf))}
      >
        <i className="fa fa-binoculars mr-2" />
        Screenshot all devices
      </Button>

      {adding && (
        <BulkCaptureDeviceForm
          storeId={storeId}
          brands={brands}
          tree={devices}
          machineCache={machineCache}
          onCreated={handleBulkCreated}
          onClose={() => setAdding(false)}
        />
      )}

      {devices.length === 0 && <p className="text-muted">No capture devices for this store.</p>}

      {devices.map((device) => {
        const cameras = camerasOf(device);
        // Discovery resolves its own server inside the panel, through the shared resolver: it
        // needs the store-wide fallback to stay usable on a device that has no camera yet.
        return (
          <Card key={device.public_id}>
            <CardHeader className="d-flex justify-content-between align-items-center">
              <CardTitle tag="h4" className="mb-0">
                {device.name || device.host || device.public_id}
              </CardTitle>
              <div>
                <Button
                  size="sm"
                  className="btn-info mr-2"
                  title="Probe a slot range on this device and turn the answers into cameras"
                  // Never disabled. Not on cameras.length — a device with no camera is the very
                  // case discovery exists for — and not on the server either: the panel owns the
                  // "no server assigned" guard and its own disabled state, so gating here would
                  // hide the explanation behind a dead button (legacy said why, see
                  // the removed legacy Scan DVR card).
                  onClick={() => toggleDiscovery(device.public_id)}
                >
                  <i className="fa fa-search-plus mr-2" />
                  Discover cameras
                </Button>
                <Button
                  size="sm"
                  className="btn-info"
                  title="Capture every active camera on this device"
                  disabled={!cameras.some(isCapturable)}
                  onClick={() => captureCameras(cameras)}
                >
                  <i className="fa fa-binoculars mr-2" />
                  Screenshot all cameras
                </Button>
              </div>
            </CardHeader>
            <CardBody>
              {discovering[device.public_id] && (
                <DeviceDiscoveryPanel
                  storeId={storeId}
                  device={device}
                  tree={tree}
                  machineCache={machineCache}
                  brandKind={brandKindOf(device.brand, brandKinds)}
                  brandKinds={brandKinds}
                  onApplied={() => {
                    closeDiscovery(device.public_id);
                    if (reset) reset();
                  }}
                />
              )}
              <Table responsive size="sm">
                <thead>
                  <tr>
                    <th>Camera (slot)</th>
                    <th>Name</th>
                    <th>Protocol</th>
                    <th>DVR type</th>
                    <th>DVR IP</th>
                    <th>RTSP port</th>
                    <th>Machine</th>
                    <th>Stream</th>
                    <th>Screenshot</th>
                  </tr>
                </thead>
                <tbody>
                  {cameras.map((camera) => (
                    <tr key={camera.public_id} className={camera.is_active ? undefined : "text-muted"}>
                      <td>
                        {camera.slot_number}
                        {cameraHasPolygon(camera) && (
                          <i className="fa fa-draw-polygon ml-2 text-info" title="Has analysis polygon" />
                        )}
                      </td>
                      <td>
                        {cameraName(camera)}
                        {!camera.is_active && (
                          <Badge color="secondary" className="ml-2">
                            inactive
                          </Badge>
                        )}
                      </td>
                      <td>{camera.protocol ? <Badge color="info">{camera.protocol}</Badge> : "—"}</td>
                      <td>{brandName(device.brand)}</td>
                      <td>{device.host || "—"}</td>
                      <td>{device.rtsp_port ?? "—"}</td>
                      <td>
                        {camera.inference_machine != null
                          ? machineCache[camera.inference_machine] || `#${camera.inference_machine}`
                          : "—"}
                      </td>
                      <td>{camera.stream_quality || "MAIN"}</td>
                      <td>
                        <CameraScreenshot
                          storeId={storeId}
                          cameraPublicId={camera.public_id}
                          capturable={isCapturable(camera)}
                          label={`Camera ${camera.slot_number}`}
                          batchResult={batchResults[camera.public_id]}
                          downloadName={`${camera.public_id}.jpg`}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </CardBody>
          </Card>
        );
      })}
    </div>
  );
}

CameraModelScreenshots.propTypes = {
  storeId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  storeLocation: PropTypes.string,
};
